import React, { useState, useEffect, useCallback } from 'react';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import trident from '../assets/odin-trident.png';
import '../dashboard/dashboard.css';
import './InspectionScheduling.css';
import './OdinWorkspaceTheme.css';
import Sidebar from '../Components/Sidebar';
import axios from '../api/axiosInstance';
import { logout } from '../auth/cookieUtils';

const InspectionScheduling = () => {

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [activeTab, setActiveTab] = useState('due-unscheduled');
  const [inspections, setInspections] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [inspectionMetrics, setInspectionMetrics] = useState({
    total: 0,
    internal: 0,
    external: 0,
    ut: 0
  });
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    scheduledDate: '',
    outageType: '',
    inspectionCompany: '',
    jobScope: ''
  });
  const [completionForm, setCompletionForm] = useState({
    completedDate: ''
  });
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleLogout = () => {
    logout();
  };

  const handleMarkComplete = (scheduleId) => {
    setSelectedScheduleId(scheduleId);
    setShowCompleteModal(true);
  };

  const submitMarkComplete = async () => {
    if (!selectedScheduleId || !completionForm.completedDate) {
      setErrorMessage('Select a completed date before marking the inspection complete.');
      return;
    }
    try {
      setErrorMessage('');
      await axios.post(`/api/inspections/mark-complete`, null, {
        params: {
          scheduleId: selectedScheduleId,
          completedDate: completionForm.completedDate
        }
      });
      setShowCompleteModal(false);
      setSelectedScheduleId(null);
      setCompletionForm({ completedDate: '' });
      fetchInspections();
    } catch (error) {
      setErrorMessage('Failed to mark inspection complete.');
    }
  };

  const calculateInspectionMetrics = useCallback((data) => {
    const total = data.length;
    const internal = data.filter(item => item.inspectionType?.toLowerCase() === 'internal').length;
    const external = data.filter(item => item.inspectionType?.toLowerCase() === 'external').length;
    const ut = data.filter(item => item.inspectionType?.toLowerCase() === 'ut').length;
    setInspectionMetrics({ total, internal, external, ut });
  }, []);

  const fetchInspections = useCallback(async () => {
    if (!selectedLocationId || !dateRange.startDate || !dateRange.endDate) return;
    try {
      setErrorMessage('');
      let endpoint = '';
      if (activeTab === 'scheduled') {
        endpoint = `/api/inspections/scheduled?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&locationId=${selectedLocationId}`;
      } else if (activeTab === 'history') {
        endpoint = `/api/inspections/history?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&locationId=${selectedLocationId}`;
      } else {
        endpoint = `/api/inspections/due-unscheduled?locationId=${selectedLocationId}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      }
      const response = await axios.get(endpoint);
      const data = Array.isArray(response.data) ? response.data : [];
      setInspections(data);
      if (activeTab === 'scheduled') calculateInspectionMetrics(data);
    } catch (error) {
      setErrorMessage('Error fetching inspections.');
    }
  }, [activeTab, calculateInspectionMetrics, dateRange.endDate, dateRange.startDate, selectedLocationId]);

  useEffect(() => {
    const today = new Date();
    const future = new Date(today);
    future.setDate(today.getDate() + 30);
    setDateRange({
      startDate: today.toISOString().split('T')[0],
      endDate: future.toISOString().split('T')[0],
    });
  }, []);

  useEffect(() => {
    setInspections([]);
    setInspectionMetrics({ total: 0, internal: 0, external: 0, ut: 0 });
    if (!selectedLocationId) return;
    fetchInspections();
  }, [fetchInspections, selectedLocationId]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyDateRange = () => {
    if (!dateRange.startDate || !dateRange.endDate || dateRange.startDate > dateRange.endDate) {
      setErrorMessage('Select a valid date range.');
      return;
    }
    fetchInspections();
  };

  const openScheduleModal = (equipment) => {
    setSelectedEquipment(equipment);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEquipment(null);
    setScheduleForm({ scheduledDate: '', outageType: '', inspectionCompany: '', jobScope: '' });
  };

  const handleScheduleSubmit = async () => {
    if (!selectedEquipment?.equipmentId || !scheduleForm.scheduledDate) {
      setErrorMessage('Equipment and scheduled date are required.');
      return;
    }
    try {
      setErrorMessage('');
      await axios.post('/api/inspections/schedule-full', null, {
        params: {
          equipmentId: selectedEquipment.equipmentId,
          scheduledDate: scheduleForm.scheduledDate,
          inspectionType: selectedEquipment.inspectionType,
          outageType: scheduleForm.outageType || '',
          inspectionCompany: scheduleForm.inspectionCompany || '',
          jobScope: scheduleForm.jobScope || ''
        }
      });
      closeModal();
      fetchInspections();
    } catch (error) {
      setErrorMessage('Failed to schedule inspection and link record.');
    }
  };

  const memoizedLocationChange = useCallback((id) => setSelectedLocationId(id), []);

  return (
    <div className="dashboard-page odin-workspace-page inspection-workspace-page">
      <div className="dashboard-topbar">
        <div className="topbar-left">
          <img src={trident} alt="ODIN Logo" className="navbar-logo" />
          <CompanyLocationSelector
            onLocationChange={memoizedLocationChange}
          />
        </div>
        <div className="topbar-center">
          <span className="page-title">Inspection Scheduling</span>
        </div>
        <div className="topbar-right">
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="dashboard-main">
        <Sidebar />
        <main className="dashboard-content workspace-content">
          <header className="workspace-page-heading">
            <span className="workspace-page-eyebrow">Inspection planning</span>
            <h1>Inspection Scheduling</h1>
            <p>Plan upcoming work, review scheduled inspections, and track completed inspection history.</p>
          </header>
          {errorMessage && <div className="error-banner">{errorMessage}</div>}
          <div className="inspection-scheduler-tabs-container">
            <div className="inspection-scheduler-tabs">
              <button type="button" className={activeTab === 'due-unscheduled' ? 'active' : ''} onClick={() => handleTabClick('due-unscheduled')}>Due and Unscheduled</button>
              <button type="button" className={activeTab === 'scheduled' ? 'active' : ''} onClick={() => handleTabClick('scheduled')}>Scheduled Inspections</button>
              <button type="button" className={activeTab === 'history' ? 'active' : ''} onClick={() => handleTabClick('history')}>Inspection History</button>
            </div>
            <div className="date-picker-controls">
              <input type="date" name="startDate" value={dateRange.startDate} onChange={handleDateChange} />
              <span>to</span>
              <input type="date" name="endDate" value={dateRange.endDate} onChange={handleDateChange} />
              <button type="button" onClick={handleApplyDateRange}>Apply</button>
            </div>
          </div>

          {activeTab === 'scheduled' && (
  <div className="inspection-metrics">
    <div className="metric"><p>Inspections in Range</p><h2>{inspectionMetrics.total}</h2></div>
    <div className="metric"><p>Internal Inspections</p><h2>{inspectionMetrics.internal}</h2></div>
    <div className="metric"><p>External Inspections</p><h2>{inspectionMetrics.external}</h2></div>
    <div className="metric"><p>UT Inspections</p><h2>{inspectionMetrics.ut}</h2></div>
  </div>
)}


          <div className="inspection-scheduler-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Area</th>
                  <th>Insp Type</th>
                  {activeTab !== 'due-unscheduled' && <th>Outage Type</th>}
                  {activeTab !== 'history' && <th>Last Insp</th>}
                  {activeTab !== 'due-unscheduled' && <th>Interval</th>}
                  <th>Deadline</th>
                  {activeTab === 'scheduled' && <th>Scheduled Date</th>}
                  {activeTab !== 'due-unscheduled' && <th>Inspection Company</th>}
                  {activeTab === 'due-unscheduled' && <th>Action</th>}
                  {activeTab === 'scheduled' && <th>Actions</th>}
                </tr>
               

              </thead>
              <tbody>
                {inspections.length > 0 ? inspections.map(inspection => (
                  <tr key={inspection.id || inspection.equipmentId}>
                    <td>{inspection.equipmentName}</td>
                    <td>{inspection.areaName}</td>
                    <td>{inspection.inspectionType}</td>
                    {activeTab !== 'due-unscheduled' && <td>{inspection.outageType || '-'}</td>}
                    {activeTab !== 'history' && <td>{inspection.lastInspectionDate || '-'}</td>}
                    {activeTab !== 'due-unscheduled' && (
                    <td>{inspection.inspectionInterval ?? '-'}</td>)}
                    <td className={inspection.currentDeadline && new Date(inspection.currentDeadline) < new Date()
    ? 'overdue'
    : ''
}>
  {inspection.currentDeadline || '-'}
</td>

                    {activeTab === 'scheduled' && <td>{inspection.scheduledDate || '-'}</td>}
                    {activeTab !== 'due-unscheduled' && <td>{inspection.inspectionCompany || '-'}</td>}
                    {activeTab === 'due-unscheduled' && (
                      <td>
                        <button type="button" className="schedule-button" onClick={() => openScheduleModal(inspection)}>
                          Schedule
                        </button>
                      </td>
                    )}
                    {activeTab === 'scheduled' && (
  <td>
    <button
      type="button"
      className="mark-complete-button"
      onClick={() => handleMarkComplete(inspection.id)}
    >
      Mark Complete
    </button>
    <button
  type="button"
  className="delete-button"
  onClick={() => {
    setDeleteTargetId(inspection.id);
    setShowDeleteConfirm(true);
  }}
>
  Delete
</button>

  </td>
)}

                  </tr>
                )) : (
                  <tr className="no-data-row">
                    <td colSpan="10">
                      <div className="no-data-message">
                        <span>🗂️</span>
                        <p>No inspections found in this range.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>

      {showModal && selectedEquipment && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Schedule Inspection</h2>
            <p><strong>Equipment:</strong> {selectedEquipment.equipmentName}</p>
            <label>
              Scheduled Date:
              <input
                type="date"
                value={scheduleForm.scheduledDate}
                onChange={(e) => setScheduleForm({ ...scheduleForm, scheduledDate: e.target.value })}
              />
            </label>
            <label>
              Inspection Type:
              <input type="text" value={selectedEquipment.inspectionType} readOnly />
            </label>
            <label>
              Outage Type:
              <input
                type="text"
                value={scheduleForm.outageType}
                onChange={(e) => setScheduleForm({ ...scheduleForm, outageType: e.target.value })}
              />
            </label>
            <label>
              Inspection Company:
              <input
                type="text"
                value={scheduleForm.inspectionCompany}
                onChange={(e) => setScheduleForm({ ...scheduleForm, inspectionCompany: e.target.value })}
              />
            </label>
            <label>
  Job Scope:
  <input
    type="text"
    value={scheduleForm.jobScope}
    onChange={(e) => setScheduleForm({ ...scheduleForm, jobScope: e.target.value })}
  />
</label>

            <div className="modal-buttons">
              <button type="button" onClick={handleScheduleSubmit}>Submit</button>
              <button type="button" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

{showDeleteConfirm && (
  <div className="modal-overlay">
    <div className="modal-content">
      <h3>Confirm Deletion</h3>
      <p>Are you sure you want to delete this scheduled inspection?</p>
      <div className="modal-buttons">
        <button
          type="button"
          onClick={async () => {
            try {
              await axios.delete(`/api/inspections/delete?scheduleId=${deleteTargetId}`);
              setShowDeleteConfirm(false);
              setDeleteTargetId(null);
              fetchInspections(); // Refresh list
            } catch (error) {
              console.error('Failed to delete inspection:', error);
              setErrorMessage('Failed to delete the scheduled inspection.');
            }
          }}
        >
          Yes, Delete
        </button>
        <button
          type="button"
          onClick={() => {
            setShowDeleteConfirm(false);
            setDeleteTargetId(null);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{showCompleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Complete Inspection</h3>
            <label>
              Completed Date:
              <input
                type="date"
                value={completionForm.completedDate}
                onChange={(e) => setCompletionForm({ completedDate: e.target.value })}
              />
            </label>
            <div className="modal-buttons">
              <button type="button" onClick={submitMarkComplete}>Submit</button>
              <button type="button" onClick={() => {
                setShowCompleteModal(false);
                setSelectedScheduleId(null);
                setCompletionForm({ completedDate: '' });
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectionScheduling;
